'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { userAuthKeys } from './useUserAuth';
import { refreshSessionCookie } from '../utils/sessionRefresh';
import { recordSessionRefreshOutcome } from './useSessionHealth';
import { createLogger } from '../utils/logger';

const logger = createLogger('SessionKeepAlive');

/**
 * How long a minted session cookie is allowed to age before we re-mint it.
 *
 * The server issues 24h cookies without `rememberMe` and 14d with it
 * (`apps/api/components/routes/auth.ts`). Six hours keeps us comfortably
 * inside even the short variant while costing at most four cheap API calls a
 * day for a tab that stays open.
 */
const REFRESH_AFTER_MS = 6 * 60 * 60 * 1000;

/** How often we re-evaluate whether a refresh is due. */
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Shared across tabs on the same origin so N open tabs perform ONE re-mint
 * per window rather than N.
 */
const LAST_MINT_STORAGE_KEY = 'orderup:session-last-mint';

function readLastMint(): number | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(LAST_MINT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        // Private mode / storage disabled — fall back to always-refresh-on-timer.
        return null;
    }
}

function writeLastMint(at: number): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(LAST_MINT_STORAGE_KEY, String(at));
    } catch {
        // Non-fatal: we lose cross-tab coordination, not correctness.
    }
}

/**
 * Keeps the server session cookie alive for an active user.
 *
 * Without this, nothing ever refreshed the cookie: `refreshSessionCookie` was
 * only called REACTIVELY, from the apiClient's 401 interceptor. So the cookie
 * simply expired on its own schedule, and the next page load found no session
 * and bounced the user to the auth app — which, finding a live Firebase login,
 * signed them back in and returned them. That is the "it logs me out and
 * straight back in every morning" behaviour.
 *
 * Mounted once at the provider root, next to the auth subscription. Re-mints:
 *   - when the tab becomes visible again (the common "back after lunch" case)
 *   - when the browser comes back online
 *   - on a slow timer for tabs that are left open and focused
 *
 * Anonymous users are skipped — they authenticate with Firebase ID tokens on
 * each request and have no server session cookie to keep alive.
 */
export const useSessionKeepAlive = (): void => {
    const { data: authData } = useAuth();
    const { authUser, authResolved } = authData;
    const queryClient = useQueryClient();
    const inFlightRef = useRef(false);

    const uid = authUser?.uid;
    const isAnonymous = authUser?.isAnonymous ?? false;

    useEffect(() => {
        if (!authResolved || !uid || isAnonymous) return;

        // Seed the clock on first sight, but ONLY on evidence that a session
        // actually exists.
        //
        // ⚠️ THIS USED TO SEED UNCONDITIONALLY, on the reasoning that "a cold
        // load has just established (or just recovered) its session". A cold
        // load with an ALREADY-DEAD cookie is the exact case this hook exists
        // to prevent, and the unconditional stamp recorded a mint that never
        // happened — so the hook stood down for a full REFRESH_AFTER_MS (6h)
        // over a session that was dead on arrival (prod 2026-09-09).
        //
        // `getQueryData` is a CACHE READ, never a fetch: the same key is
        // populated by `useUserSessionValidation` and seeded by
        // `useUserSignIn.onSuccess`, so a genuine live session is already
        // evidenced by the time this matters and costs nothing to confirm.
        // Absent evidence we leave the clock null, which reads as DUE — the
        // safe direction, since a needless re-mint costs one cheap call while a
        // missed one costs the session.
        const hasConfirmedSession = (): boolean =>
            queryClient.getQueryData(userAuthKeys.session()) != null;

        if (readLastMint() === null && hasConfirmedSession()) {
            writeLastMint(Date.now());
        }

        const maybeRefresh = async (): Promise<void> => {
            if (inFlightRef.current) return;
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

            let lastMint = readLastMint();
            // Session validation may have resolved AFTER the seed above (cold
            // boot ordering). Take the evidence now rather than re-minting a
            // cookie that was fine all along.
            if (lastMint === null && hasConfirmedSession()) {
                writeLastMint(Date.now());
                lastMint = readLastMint();
            }
            if (lastMint !== null && Date.now() - lastMint < REFRESH_AFTER_MS) return;

            inFlightRef.current = true;
            try {
                const refreshed = await refreshSessionCookie();
                // Record EVERY outcome, success included — a success is what
                // clears a previously-raised expiry, so the notice comes down
                // on recovery without needing a reload.
                const health = recordSessionRefreshOutcome(queryClient, refreshed);
                if (refreshed) {
                    writeLastMint(Date.now());
                    logger.debug('Session cookie proactively re-minted');
                } else if (health.expired) {
                    // ⚠️ THIS USED TO BE A BARE logger.warn AND NOTHING ELSE.
                    // The re-mint then failed quietly on every tick forever
                    // while the user kept working against a dead session, and
                    // every server write after that was rejected with nothing
                    // on screen explaining why. Observed in production
                    // 2026-09-06: an admin save spun on "Updating…" while the
                    // console filled with this warning and the Firestore Write
                    // channel returned 400. ONE actionable message at the
                    // moment it goes wrong beats N confusing failures after it.
                    logger.error(
                        'Session is dead after consecutive failed re-mints — surfacing to the user',
                        { consecutiveFailures: health.consecutiveFailures },
                    );
                } else {
                    logger.warn('Proactive session re-mint failed — will retry on next check', {
                        consecutiveFailures: health.consecutiveFailures,
                    });
                }
            } finally {
                inFlightRef.current = false;
            }
        };

        const onWake = () => {
            void maybeRefresh();
        };

        const interval = setInterval(onWake, CHECK_INTERVAL_MS);

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onWake);
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('online', onWake);
        }

        return () => {
            clearInterval(interval);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onWake);
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('online', onWake);
            }
        };
        // `queryClient` is the stable provider instance, not query DATA — it
        // does not change per render, so listing it cannot re-arm the timer.
        // The session evidence is read imperatively inside the callbacks
        // precisely so that resolving it later does NOT tear down and rebuild
        // the listeners on every cold boot.
    }, [authResolved, uid, isAnonymous, queryClient]);
};
